<template>
  <div id="product-form">
    <b-form-group label="paper">
      <span v-if="restrictProduct">{{paper}} - {{corners}}</span>
      <b-select v-model="paper" v-else>
        <optgroup v-for="(options, label) in paperOptionGroups" :label="label" :key="label">
          <option v-for="value in options" :value="value" :key="value">{{value}}</option>
        </optgroup>
      </b-select>
    </b-form-group>
    <b-form-group label="quantity">
      <span v-if="restrictQty">{{quantity}}</span>
      <b-form-radio-group v-else v-model="quantity" buttons :options="quantityOptions"/>
    </b-form-group>
    <b-form-group label="corners" v-if="!restrictProduct">
      <b-form-radio-group v-model="corners" buttons :options="cornerOptions"/>
    </b-form-group>

    <div class="price" v-if="price">
      <label>Your price</label>
      <span class="amount">${{price.toFixed(2)}}</span>
      <div class="saving-icon" v-if="saving"><i class="fas fa-spinner fa-spin"/></div>
      <b-button v-else-if="!autoSave" @click="save">Save</b-button>
    </div>
  </div>
</template>

<script>
import {products} from '@/services/products.js';
import {groupBy, pick, uniq, flatten, sortBy, mapValues} from 'lodash';
import {mapActions, mapGetters} from 'vuex';
import {compressed} from '@/lib/selectedProduct';
import {isRounded} from "@/lib/productAttrs";

// Common function that is invoked by watch on paper, quantity and corners.
// Trigger save upon change and 250 ms after user has stopped fiddling
const triggerSave = _.debounce(function (val, old) {
  if (this.autoSave && old !== null) this.save();
}, 250, {leading: true});

export default {
  props: {
    line: Object,
    autoSave: {type: Boolean},
    restrictQty: {type: Boolean},
    restrictProduct: {type: Boolean},
  },
  data() {
    return {
      products: [],
      productUrls: {},
      paperOptionGroups: [],
      paper: null,
      quantity: null,
      corners: null,
      saving: true
    }
  },
  async created() {
    await this.loadProducts();
    this.setCurrentValues();
  },
  computed: {
    price() {
      return this.info && this.info.price;
    },
    quantityOptions() {
      let quantities = this.selectedProducts.map(p => Object.keys(p.prices));
      return sortBy(uniq(flatten(quantities)), parseInt);
    },
    cornerOptions() {
      // Always put square first
      // reverse the array so we sort by highest index first and unfound items (-1) are last
      let order = ['Square', 'Rounded'].reverse();
      let sortFn = (a, b) => order.indexOf(b) - order.indexOf(a);
      return uniq(this.selectedProducts.map(p => p.attrs.corners)).sort(sortFn);
    },
    selectedProducts() {
      return this.products.filter(({name}) => name === this.paper);
    },
    info() {
      // Return url, price for current # of sides.
      // If the # of sides isn't an option for chosen product,
      // choose a valid # of sides and add a 'sides' key.
      // TODO caller should do something intelligent with the 'sides' key
      // TODO what about corners mismatch
      let otherSide = null;
      for (let p of this.selectedProducts) {
        if (p.attrs.corners !== this.corners) continue;
        let price = p.prices[this.quantity];
        let url = p.urls[this.quantity];
        if (+p.attrs.sides === this.numSides) {
          return {price, url}
        } else {
          otherSide = {price, url, sides: p.attrs.sides};
        }
      }
      console.log('not a perfect match ->', otherSide);
      return otherSide;
    },
    numSides() {
      // Use preview_urls as proxy for nunber of pages on the design (so we don't load)
      // anything else here
      return this.line.preview_urls.length;
    }
  },
  watch: {
    quantity: triggerSave,
    paper: triggerSave,
    corners: triggerSave
  },
  methods: {
    ...mapActions(['selectProduct']),
    async loadProducts() {
      this.products = this.decorateProducts(await products('any'));
      this.calcOptions();
    },
    setCurrentValues() {
      console.log(this.line);
      this.quantity = +this.line.boxSize;
      try{
        this.paper = this.productUrls[this.line.product.url].name;
        console.log('paper->', this.paper);
      } catch(e) {
        console.error('cant set paper from url', e);
        console.log(this.line.product.url, this.productUrls, this.productUrls[this.line.product.url]);
        console.log(JSON.stringify(this.productUrls, null, 4));
      }
      this.corners = isRounded(this.line.product) ? 'Rounded' : 'Square';
      this.saving = false;
    },
    decorateProducts(products) {
      // decorate the products with an unpacked attrs, derived from product.attributes
      // same with prices
      // Also set productUrls
      for (let p of products) {
        p.attrs = {};
        for (let {sku_tag} of p.product.attributes) {
          let [key, value] = sku_tag.split(':');
          p.attrs[key] = value;
        }
        p.prices = {};
        p.urls = {};
        for (let {quantity, retail_price, stockrecord} of p.product.prices) {
          p.prices[quantity] = retail_price;
          p.urls[quantity] = stockrecord.product;
          this.productUrls[stockrecord.product] = p;
        }
      }
      return products;
    },
    calcOptions() {
      console.log(this.products);
      function category_title(product) {
        let s = product.category_name;
        s = s.replace('colorcore', 'color core');
        return s.replace(/\b[a-z]/g, m => m[0].toUpperCase());
      }
      let categorized = groupBy(this.products, category_title);
      console.log(categorized);
      this.paperOptionGroups = mapValues(categorized, l => uniq(l.map(p => p.name)));
    },
    async save() {
      console.log('save');
      if (this.saving) return console.log('already saving');
      if (!this.line) return console.log('no selected line');
      if (!this.info) return console.log('no info');
      if (this.saving || !this.line || !this.info) return;
      console.log('saving...');

      // Event handler can call:
      // await evt.saving(this.action(...));
      // ...for spinner to show
      this.$emit('save', {
        boxSize: this.quantity,
        product: this.info.url,

        saving: async promise => {
          this.saving = true;
          await promise;
          this.saving = false;
        },
        start: () => this.saving = true,
        done: () => this.saving = false
      });
    }
  },
}
</script>

<style lang="scss" scoped>
#product-form {
  // Just wide enough to fit all content when loaded
  width: 433px;
}

h1 {
  font-size: 1.4em;
  text-align: center;
  line-height: 3em;
  text-transform: uppercase;
}

.price {
  display: flex;
  align-items: center;
  padding: 0.5em 1em;

  label {
    color: #aaa;
    font-size: small;
    text-transform: uppercase;
    margin: 0;
  }
  .amount {
    padding-left: 10px;
    font-size: 21px;
    flex-grow: 3;
  }
}
</style>
<style lang="scss">
  @import '~scss/colors';
// This needs to affect the form-group within the b-form-group,
// which won't happen unless we use unscoped css
#product-form {
  z-index:3;
  .form-group {
    border: none;
    padding: 0 1em 1em 1em;
  }
  .col-form-legend {
    color: #aaa;
    font-size: small;
    text-transform: uppercase;
    margin: 0;
    border: 1px color red;
  }
  fieldset {
    margin: 0;
    padding: 0;
  }
  .btn-group {
    width: 100%;
    label {
      flex-grow: 1;
    }
  }

  // Buttons are used to provide the radio group
  // Override the application level button look (square with caps)
  .btn-group[role="radiogroup"] {
    border: 1px solid $gray-2;
    label.btn {
      box-shadow: none !important;
      font-weight: normal;
      text-transform: uppercase;
      border-radius: 0;
      font-size: 12px;
      line-height: 2.5em;
      padding: 0.5em 1em;
      height: auto;
      letter-spacing: 2px;
      outline: none !important;
      background-color: #f8f8f8;
      color: #ccc;
      border: none;
      &.active {
        background-color: white;
        color: black;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/shared/SharedLineItemProductForm.vue